import {getBaseFetchHeaders} from '@github-ui/fetch-headers'
import {responseErrorForStatus} from '@github-ui/react-core/future/response-error'

type AutoFlushingQueueEntry<ElementType> = [ElementType, string]
class AutoFlushingQueue<ElementType = HTMLElement> {
  timeout: number
  limit: number
  elements: Array<AutoFlushingQueueEntry<ElementType>> = []
  timer: number | null = null
  callbacks: Array<(elementsToFlush: Array<AutoFlushingQueueEntry<ElementType>>) => Promise<void>> = []
  index: number

  constructor(timeout = 50, limit = 30) {
    this.timeout = timeout // flush timeout in milliseconds
    this.limit = limit // max number of elements before autoflushing
    this.index = 0
  }

  push(element: ElementType): string {
    const key = `item-${this.index++}`

    if (this.timer) {
      window.clearTimeout(this.timer)
      this.timer = null
    }
    if (this.elements.length >= this.limit) {
      this.#flush()
    }

    this.timer = window.setTimeout(() => {
      this.timer = null
      this.#flush()
    }, this.timeout)

    this.elements.push([element, key])
    return key
  }

  onFlush(callback: (elementsToFlush: Array<AutoFlushingQueueEntry<ElementType>>) => Promise<void>): void {
    this.callbacks.push(callback)
  }

  async #flush(): Promise<void> {
    const elementsToFlush = this.elements.splice(0, this.limit)
    if (elementsToFlush.length === 0) return

    await Promise.all(this.callbacks.map(callback => callback(elementsToFlush)))
  }
}

interface HTMLElementWithInputs extends HTMLElement {
  inputs: HTMLInputElement[]
}

type BatchLoaderFallback<ResponseType> = (
  error: Error,
  shouldReport: boolean,
) => {value: ResponseType; reported: boolean}

export type OnError = ({
  elements,
  formData,
  error,
}: {
  elements: HTMLElement[]
  formData: FormData
  response?: Response
  error: unknown
}) => void

export class BatchLoader<ResponseType> {
  autoFlushingQueue: AutoFlushingQueue<HTMLElementWithInputs>
  url: string
  callbacks: Map<string, (value: ResponseType) => void>
  validate: (value: unknown) => asserts value is ResponseType
  onError?: OnError
  fallback: BatchLoaderFallback<ResponseType> | undefined
  // Keep one actionable fallback report per loader so repeated failures from the
  // same batched endpoint do not flood Failbot. Expected failures keep the slot available.
  fallbackReported = false

  constructor(
    url: string,
    validator: (value: unknown) => asserts value is ResponseType,
    onError?: OnError,
    fallback?: BatchLoaderFallback<ResponseType>,
  ) {
    this.url = url
    this.callbacks = new Map()
    this.autoFlushingQueue = new AutoFlushingQueue()
    this.autoFlushingQueue.onFlush(async elements => {
      this.load(elements)
    })
    this.validate = validator
    this.fallback = fallback
    this.onError = onError
  }

  loadInBatch(element: HTMLElementWithInputs): Promise<ResponseType> {
    const key = this.autoFlushingQueue.push(element)
    return new Promise(resolve => this.callbacks.set(key, resolve))
  }

  async load(elements: Array<AutoFlushingQueueEntry<HTMLElementWithInputs>>) {
    const elementsByKey = new Map<string, HTMLElementWithInputs>()

    for (const [batchElement, key] of elements) {
      elementsByKey.set(key, batchElement)
    }

    const consolidatedData = new FormData()

    for (const [key, batchElement] of elementsByKey.entries()) {
      for (const input of batchElement.inputs) {
        consolidatedData.append(`items[${key}][${input.name}]`, input.value)
      }
    }

    if (Array.from(consolidatedData.values()).length === 0) {
      if (this.fallback) {
        this.#resolveWithFallback(
          new Map(Array.from(elementsByKey.keys(), key => [key, new Error('Batch request has no input data')])),
        )
      }
      return
    }

    consolidatedData.set('_method', 'GET')
    // Only loaders with a fallback recover from batch failures. For other callers,
    // preserve the original response handling, including throwing on malformed responses.
    if (!this.fallback) {
      let response: Response
      try {
        response = await this.#fetch(consolidatedData)
      } catch (error) {
        if (!this.onError) throw error
        this.onError({
          elements: elements.map(([element]) => element),
          formData: consolidatedData,
          response: undefined,
          error,
        })
        return
      }

      if (response.ok) {
        const json = await response.json()

        if (!json || typeof json !== 'object' || Array.isArray(json)) {
          throw new Error('Malformed batch response')
        }

        for (const key in json) {
          const callback = this.callbacks.get(key)
          if (callback) {
            const value = json[key]
            this.validate(value)
            callback(value)
            this.callbacks.delete(key)
          }
        }
      } else {
        this.onError?.({
          elements: elements.map(([element]) => element),
          formData: consolidatedData,
          response,
          error: new Error('Batch request failed'),
        })
      }
      return
    }

    let response: Response | undefined
    try {
      response = await this.#fetch(consolidatedData)
      if (!response.ok) {
        this.onError?.({
          elements: elements.map(([element]) => element),
          formData: consolidatedData,
          response,
          error: new Error('Batch request failed'),
        })
        throw await responseErrorForStatus(response)
      }

      const json = await response.json()
      if (!json || typeof json !== 'object' || Array.isArray(json)) {
        throw new Error('Malformed batch response')
      }

      const failedKeys = new Map<string, Error>()
      for (const key of elementsByKey.keys()) {
        if (!Object.hasOwn(json, key)) {
          failedKeys.set(key, new Error(`Batch response missing ${key}`))
          continue
        }

        try {
          const value = json[key]
          this.validate(value)
          this.callbacks.get(key)?.(value)
          this.callbacks.delete(key)
        } catch (error) {
          failedKeys.set(key, error instanceof Error ? error : new Error(String(error)))
        }
      }

      if (failedKeys.size > 0) {
        this.#resolveWithFallback(failedKeys)
      }
    } catch (error) {
      if (!response) {
        this.onError?.({
          elements: elements.map(([element]) => element),
          formData: consolidatedData,
          response,
          error,
        })
      }
      const failure = error instanceof Error ? error : new Error(String(error))
      this.#resolveWithFallback(new Map(Array.from(elementsByKey.keys(), key => [key, failure])))
    }
  }

  #fetch(consolidatedData: FormData): Promise<Response> {
    return fetch(this.url, {
      method: 'POST',
      body: consolidatedData,
      headers: {
        Accept: 'application/json',
        ...getBaseFetchHeaders(),
      },
    })
  }

  #resolveWithFallback(failedKeys: Map<string, Error>): void {
    const fallback = this.fallback
    if (!fallback) return

    const fallbackResult = fallback(
      failedKeys.values().next().value ?? new Error('Batch request failed'),
      !this.fallbackReported,
    )
    this.fallbackReported ||= fallbackResult.reported
    for (const key of failedKeys.keys()) {
      this.callbacks.get(key)?.(fallbackResult.value)
      this.callbacks.delete(key)
    }
  }
}
